/**
 * MaterializedView - Reactive, fluent API for client-side data transformations
 *
 * Allows chaining operations like filter, orderBy, slice, map on GraphStore data
 * while maintaining reactivity. Ideal for DAG traversal with client-side ordering.
 *
 * @example
 * ```typescript
 * const recentSteps = useNodes(thread.steps)
 *   .filter(s => s.role === ChatRoleType.USER)
 *   .orderBy('created_at')
 *   .slice(0, 10);
 *
 * // Use like a normal array
 * return <>{recentSteps.map(s => <div key={s.id}>{s.text}</div>)}</>;
 * ```
 */

import { useMemo } from 'react';

type Comparator<T> = (a: T, b: T) => number;
type Predicate<T> = (item: T) => boolean;
type Mapper<T, U> = (item: T) => U;

/**
 * Transformation function that takes an array and returns a transformed array
 */
type Transformation<T> = (data: T[]) => T[];

/**
 * Proxy handler to enable array index access on MaterializedView
 * Declared before class so it can be used in methods
 */
const materializedViewHandler: ProxyHandler<any> = {
  get(target, prop, receiver) {
    // Handle numeric indices
    if (typeof prop === 'string' && /^\d+$/.test(prop)) {
      const index = parseInt(prop, 10);
      return target.value()[index];
    }

    // Handle length property
    if (prop === 'length') {
      return target.value().length;
    }

    // Default behavior for methods and other properties
    return Reflect.get(target, prop, receiver);
  },
};

/**
 * MaterializedView wraps an array with a fluent transformation API
 * All methods return a new MaterializedView, allowing chaining
 */
export class MaterializedView<T> implements Iterable<T> {
  private data: T[];
  private transformations: Transformation<T>[] = [];

  // Memoization cache
  private cachedResult?: T[];
  private lastInputRef?: T[];

  constructor(data: T[], transformations: Transformation<T>[] = []) {
    this.data = data;
    this.transformations = transformations;
  }

  /**
   * Helper to wrap a new MaterializedView with Proxy for array-like access
   */
  private wrapWithProxy(view: MaterializedView<any>): any {
    return new Proxy(view, materializedViewHandler as ProxyHandler<any>);
  }

  /**
   * Filter items by predicate or property existence
   * @param predicate Function to test each item, or property name to check for truthy value
   */
  filter(predicate: Predicate<T> | keyof T): MaterializedView<T> {
    const filterFn: Predicate<T> = typeof predicate === 'function'
      ? predicate
      : (item) => Boolean(item[predicate]);

    const view = new MaterializedView(
      this.data,
      [...this.transformations, (data) => data.filter(filterFn)]
    );
    return this.wrapWithProxy(view);
  }

  /**
   * Order items by property name(s) or custom comparator
   * @param criteria Property name, array of properties (prefix with '-' for desc), or comparator function
   *
   * @example
   * .orderBy('created_at')                    // Ascending by property
   * .orderBy(['-priority', 'created_at'])     // Descending priority, then ascending created_at
   * .orderBy((a, b) => a.value - b.value)     // Custom comparator
   */
  orderBy(criteria: string | string[] | Comparator<T>): MaterializedView<T> {
    let compareFn: Comparator<T>;

    if (typeof criteria === 'function') {
      // Custom comparator
      compareFn = criteria;
    } else if (typeof criteria === 'string') {
      // Single property name
      compareFn = this.createPropertyComparator([criteria]);
    } else {
      // Array of property names (with optional '-' prefix for descending)
      compareFn = this.createPropertyComparator(criteria as string[]);
    }

    const view = new MaterializedView(
      this.data,
      [...this.transformations, (data) => [...data].sort(compareFn)]
    );
    return this.wrapWithProxy(view);
  }

  /**
   * Slice the array (like Array.prototype.slice)
   * @param start Start index (inclusive)
   * @param end End index (exclusive), optional
   */
  slice(start: number, end?: number): MaterializedView<T> {
    const view = new MaterializedView(
      this.data,
      [...this.transformations, (data) => data.slice(start, end)]
    );
    return this.wrapWithProxy(view);
  }

  /**
   * Map items to a new type
   * @param mapper Function to transform each item
   */
  map<U>(mapper: Mapper<T, U>): MaterializedView<U> {
    const view = new MaterializedView<U>(
      this.data as unknown as U[],
      [...(this.transformations as unknown as Transformation<U>[]), (data) => (data as unknown as T[]).map(mapper)]
    );
    return new Proxy(view, materializedViewHandler);
  }

  /**
   * Reduce items to a single value (terminal operation - returns the value, not a MaterializedView)
   * @param reducer Function to reduce items
   * @param initialValue Initial value for the reduction
   */
  reduce<U>(reducer: (accumulator: U, currentValue: T, currentIndex: number, array: T[]) => U, initialValue: U): U {
    return this.value().reduce(reducer, initialValue);
  }

  /**
   * Get the computed result as an array
   * This applies all transformations and returns the final array
   *
   * Results are memoized - transformations only recompute when input data changes
   */
  value(): T[] {
    // Only recompute if input data changed (by reference)
    // When the Jotai atom updates, useNodes() returns a new array reference
    if (this.cachedResult && this.lastInputRef === this.data) {
      return this.cachedResult;
    }

    // Data changed - recompute transformations
    this.lastInputRef = this.data;
    this.cachedResult = this.transformations.reduce(
      (data, transform) => transform(data),
      this.data
    );

    return this.cachedResult;
  }

  /**
   * Get the length of the computed array
   */
  get length(): number {
    return this.value().length;
  }

  /**
   * Make MaterializedView iterable (for...of, spread operator, etc.)
   */
  [Symbol.iterator](): Iterator<T> {
    return this.value()[Symbol.iterator]();
  }

  /**
   * Array index access (view[0], view[1], etc.)
   */
  [index: number]: T;

  /**
   * Helper to create a multi-property comparator
   * Supports property names with optional '-' prefix for descending order
   */
  private createPropertyComparator(properties: (keyof T | string)[]): Comparator<T> {
    return (a, b) => {
      for (const prop of properties) {
        const isDescending = typeof prop === 'string' && prop.startsWith('-');
        const key = (isDescending ? prop.slice(1) : prop) as keyof T;

        const aVal = a[key];
        const bVal = b[key];

        let comparison = 0;
        if (aVal < bVal) comparison = -1;
        else if (aVal > bVal) comparison = 1;

        if (comparison !== 0) {
          return isDescending ? -comparison : comparison;
        }
      }
      return 0;
    };
  }
}

/**
 * Create a MaterializedView from reactive data (for use in React hooks)
 * @param data Reactive array from useAtomValue or other React hook
 * @returns MaterializedView that can be chained with transformations
 *
 * @example
 * ```typescript
 * function MyComponent() {
 *   const relIds = useAtomValue(relationshipAtom);
 *   const nodes = useMemo(() => relIds.map(id => getNode(id)), [relIds]);
 *
 *   const view = useMaterializedView(nodes)
 *     .filter(n => n.active)
 *     .orderBy('created_at');
 *
 *   return <>{view.map(n => <div key={n.id}>{n.name}</div>)}</>;
 * }
 * ```
 */
export function useMaterializedView<T>(data: T[]): MaterializedView<T> {
  // Wrap data in MaterializedView
  // Note: We don't memoize the view itself because each transformation creates a new instance
  // The actual computation is lazy and happens when .value() or iteration occurs
  return new MaterializedView(data);
}

/**
 * Create a proxied MaterializedView that supports array-like access
 * This is used internally by the standalone useNodes() hook
 */
export function createMaterializedView<T>(data: T[]): MaterializedView<T> {
  const view = new MaterializedView(data);
  return new Proxy(view, materializedViewHandler);
}
